<template>
    <div>
        <v-list-item
            v-for="item in filteredBaseFeatures"
            :id="`${item.id}`"
            :key="item.title"
            :link="true"
            :to="item.url"
            active-class="list-item-active-highlight"
        >
            <template #prepend>
                <component :is="item.icon" :size="18" class="mr-2" />
            </template>
            <v-list-item-title>
                {{ $t(`workspace.tabs.${item.title}`) }}
            </v-list-item-title>
            <template v-if="item.title === 'agent'" #append>
                <v-chip label size="x-small" class="gradient-chip" color="primary">Beta</v-chip>
            </template>
        </v-list-item>
        <v-list-group class="pr-2">
            <template #activator="{ props }">
                <v-list-item
                    v-bind="props"
                    data-cy="settings-tab"
                    :title="$t('workspace.tabs_settings_title')"
                >
                    <template #prepend>
                        <SettingsIcon :size="18" class="mr-2" />
                    </template>
                </v-list-item>
            </template>
            <v-list-item
                v-for="item in filteredSettings"
                :id="`${item.id}`"
                :key="item.title"
                :link="true"
                :to="item.url"
                active-class="list-item-active-highlight"
            >
                <template #prepend>
                    <component :is="item.icon" :size="18" class="mr-2" />
                </template>
                <v-list-item-title>
                    {{ $t(`workspace.tabs.${item.title}`) }}
                </v-list-item-title>
            </v-list-item>
        </v-list-group>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useWorkspaceStore } from '@/stores/workspace';
import { storeToRefs } from 'pinia';

import {
    Settings2Icon,
    SettingsIcon,
    TerminalIcon,
    BlocksIcon,
    ChartColumnIcon,
    UserPlusIcon,
    SearchIcon,
    MessagesSquareIcon,
    LayersIcon,
} from 'lucide-vue-next';

// Icon mapping for workspace features
const WORKSPACE_FEATURE_ICONS = {
    search: SearchIcon,
    agent: MessagesSquareIcon,
    workflows: LayersIcon,
    settings: Settings2Icon,
    user: UserPlusIcon,
    analytics: ChartColumnIcon,
    widgets: BlocksIcon,
    apikey: TerminalIcon,
};

const workspaceStore = useWorkspaceStore();
const { availableSettings, availableBaseFeatures } = storeToRefs(workspaceStore);

// Navigation settings based on available features
const filteredSettings = computed(() => {
    return Object.keys(availableSettings.value).map((key) => ({
        title: key,
        id: key,
        url: {
            name: availableSettings.value[key].name
                ? availableSettings.value[key].name
                : `workspace-${key}`,
        },
        icon: WORKSPACE_FEATURE_ICONS[key],
        active: true,
    }));
});

// Navigation base features based on available features
const filteredBaseFeatures = computed(() => {
    return Object.keys(availableBaseFeatures.value).map((key) => ({
        title: key,
        id: key,
        url: {
            name: availableBaseFeatures.value[key].name
                ? availableBaseFeatures.value[key].name
                : `workspace-${key}`,
        },
        icon: WORKSPACE_FEATURE_ICONS[key],
        active: true,
    }));
});
</script>

<style scoped lang="scss">
:deep(.v-list-group__items) {
    border-left: 2px solid lightgray;
    margin-left: 16px;
    padding-left: 8px;
}

.v-list-group__items .v-list-item {
    padding-inline-start: 10px !important;
}

.gradient-chip {
    background: #1fa2ff; /* fallback for old browsers */
    background: -webkit-linear-gradient(
        to right,
        #a6ffcb,
        #12d8fa,
        #1fa2ff
    ); /* Chrome 10-25, Safari 5.1-6 */
    background: linear-gradient(
        to right,
        #a6ffcb,
        #12d8fa,
        #1fa2ff
    ); /* W3C, IE 10+/ Edge, Firefox 16+, Chrome 26+, Opera 12+, Safari 7+ */
    filter: saturate(1.5);
}
</style>
